import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  Box,
  Badge,
  Heading,
  Image,
  Wrap,
  Alert,
  Input,
  Checkbox,
  IconButton,
  Tabs,
  SimpleGrid,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, TabPanels, Tab, TabPanel, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { FaCalendarCheck, FaClock, FaMapMarkerAlt, FaEuroSign, FaHandshake, FaMusic, FaChurch, FaFileAlt, FaStickyNote, FaPaperclip, FaTimes } from 'react-icons/fa';
import { resolveUploadsUrl } from '../utils/staticUrl.js';
import { parseAcuerdoAdjuntosInfo } from '../utils/acuerdoAdjuntosInfo.js';
import { getEventoInicioTimestampMs } from '../utils/eventoInicio.js';
import { acuerdosAPI } from '../services/api.js';

const AcuerdoDetailsModal = ({ isOpen, onClose, acuerdo, evento, otherParty, userType }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const adjuntoPreviewCardBg = useColorModeValue('white', 'gray.700');
  const surfaceBg = useColorModeValue('gray.50', 'gray.900');
  const surfaceBorder = useColorModeValue('gray.200', 'gray.700');
  const fileRowBg = useColorModeValue('gray.50', 'gray.700');
  const eventoCardBg = useColorModeValue('blue.50', 'blue.900');
  const eventoCardBorder = useColorModeValue('blue.200', 'blue.700');
  const eventoLabelColor = useColorModeValue('blue.800', 'blue.200');
  const eventoTitleColor = useColorModeValue('blue.900', 'blue.100');
  const eventoIconColor = useColorModeValue('blue.600', 'blue.300');
  const footerBg = useColorModeValue('gray.100', 'gray.700');

  const [localAdjuntosInfo, setLocalAdjuntosInfo] = useState(acuerdo?.adjuntosInfo);
  const [newAdjuntosFiles, setNewAdjuntosFiles] = useState([]);
  const [aceptaDisclaimerAdjuntos, setAceptaDisclaimerAdjuntos] = useState(false);
  const [subiendoAdjuntos, setSubiendoAdjuntos] = useState(false);
  const inputRef = useRef(null);
  const [showFullDescripcion, setShowFullDescripcion] = useState(false);
  const [showFullNotas, setShowFullNotas] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const eventoYaEmpezo = useMemo(() => {
    if (!evento?.date) return false;
    const st = getEventoInicioTimestampMs({
      date: String(evento.date).split('T')[0],
      timeStart: evento.timeStart || evento.horaInicio || evento.hora || '',
      time: evento.hora || '',
      horaInicio: evento.horaInicio || '',
      hora: evento.hora || '',
    });
    if (!st) return false;
    return Date.now() >= st;
  }, [evento]);

  useEffect(() => {
    setLocalAdjuntosInfo(acuerdo?.adjuntosInfo);
    setNewAdjuntosFiles([]);
    setAceptaDisclaimerAdjuntos(false);
    setSubiendoAdjuntos(false);
    setShowFullDescripcion(false);
    setShowFullNotas(false);
    setTabIndex(0);
  }, [acuerdo?.id, acuerdo?.adjuntosInfo, isOpen]);

  const adjuntosList = useMemo(
    () => parseAcuerdoAdjuntosInfo(localAdjuntosInfo),
    [localAdjuntosInfo],
  );

  const MAX_ACUERDO_ADJUNTOS_UI = 10;

  const handlePickAdjuntos = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    setNewAdjuntosFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (next.length >= MAX_ACUERDO_ADJUNTOS_UI) break;
        next.push(f);
      }
      return next;
    });
  };

  const handleUploadAdjuntos = async () => {
    if (!acuerdo?.id) return;
    if (acuerdo.status !== 'PENDING') return;
    if (eventoYaEmpezo) {
      toaster.create({
        title: 'El evento ya ha pasado',
        description: 'Ya no se pueden modificar adjuntos porque la hora de inicio del evento ya pasó.',
        type: 'warning',
        duration: 6000,
        closable: true,
      });
      return;
    }
    if (newAdjuntosFiles.length === 0) return;
    if (!aceptaDisclaimerAdjuntos) {
      toaster.create({
        title: 'Aviso legal obligatorio',
        description: 'Debes aceptar el aviso legal antes de adjuntar documentos.',
        type: 'warning',
        duration: 5000,
        closable: true,
      });
      return;
    }

    setSubiendoAdjuntos(true);
    try {
      const up = await acuerdosAPI.uploadAdjuntos(acuerdo.id, newAdjuntosFiles, { aceptaDisclaimer: true });
      setLocalAdjuntosInfo(up.data?.adjuntosInfo ?? localAdjuntosInfo);
      setNewAdjuntosFiles([]);
      setAceptaDisclaimerAdjuntos(false);
      toaster.create({
        title: 'Documentos adjuntados',
        description: 'Los archivos ya están disponibles en el acuerdo.',
        type: 'success',
        duration: 4000,
        closable: true,
      });
    } catch (error) {
      console.error('Error subiendo adjuntos del acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudieron subir los documentos.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setSubiendoAdjuntos(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'orange';
      case 'ACCEPTED': return 'green';
      case 'REJECTED': return 'red';
      case 'CANCELLED': return 'gray';
      case 'RESCINDED': return 'purple';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Pendiente';
      case 'ACCEPTED': return 'Aceptado';
      case 'REJECTED': return 'Rechazado';
      case 'CANCELLED': return 'Cancelado';
      case 'RESCINDED': return 'Rescindido';
      default: return 'Desconocido';
    }
  };

  if (!acuerdo) return null;

  return (
    <Dialog.Root open={isOpen} size='xl' placement='center' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.400" backdropFilter="blur(10px)" />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header bg="purple.500" color="white" roundedTop="md">
              <HStack gap={3}>
                <Icon as={FaHandshake} w={6} h={6} />
                <Text>Detalles del Acuerdo</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger color="white" />

            <Dialog.Body py={5} bg={surfaceBg}>
              <Tabs.Root
                value={tabsControlledValue(tabIndex)}
                onValueChange={tabsOnValueChange(setTabIndex)}
                variant='subtle'
                colorPalette="purple"
                fitted
              >
                <Tabs.List mb={4}>
                  <Tab value="0">Resumen</Tab>
                  <Tab value="1">Documentos</Tab>
                  <Tab value="2">Evento</Tab>
                </Tabs.List>
                <TabPanels>
                  <TabPanel value="0" p={0}>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                      <Box bg={cardBg} borderWidth="1px" borderColor={surfaceBorder} rounded="2xl" p={5}>
                        <HStack justify="space-between" align="start" mb={4} gap={3}>
                          <VStack align="start" gap={0}>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" fontWeight="700">
                              Estado
                            </Text>
                            <HStack>
                              <Badge colorPalette={getStatusColor(acuerdo.status)} fontSize="sm" px={3} py={1} rounded="full">
                                {getStatusText(acuerdo.status)}
                              </Badge>
                              {acuerdo.precio != null && acuerdo.precio !== '' && (
                                <Badge colorPalette="green" variant="subtle" fontSize="sm" px={3} py={1} rounded="full">
                                  <HStack gap={1}>
                                    <Icon as={FaEuroSign} />
                                    <Text>{acuerdo.precio}€</Text>
                                  </HStack>
                                </Badge>
                              )}
                              <Badge colorPalette="purple" variant="subtle" fontSize="sm" px={3} py={1} rounded="full">
                                <HStack gap={1}>
                                  <Icon as={FaFileAlt} />
                                  <Text>{adjuntosList.length} adjunto(s)</Text>
                                </HStack>
                              </Badge>
                            </HStack>
                          </VStack>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" fontWeight="700">
                              Contraparte
                            </Text>
                            {otherParty ? (
                              <HStack gap={3} mt={1}>
                                <Avatar.Root size="sm"><Avatar.Fallback name={otherParty.name}>{<Icon as={userType === 'banda' ? FaChurch : FaMusic} />}</Avatar.Fallback><Avatar.Image src={otherParty.avatar} /></Avatar.Root>
                                <VStack align="start" gap={0} maxW="260px">
                                  <Text fontWeight="800" lineClamp={1}>{otherParty.name}</Text>
                                  <Text fontSize="xs" color={textColor}>
                                    {userType === 'banda' ? 'Cofradía' : 'Banda'}
                                  </Text>
                                </VStack>
                              </HStack>
                            ) : (
                              <Text fontSize="sm" color={textColor} mt={1}>—</Text>
                            )}
                          </Box>
                        </HStack>

                        <Alert.Root status="warning" variant="subtle" borderRadius="xl">
                          <Alert.Indicator />
                          <Box>
                            <Alert.Title fontSize="sm">Cadencya no intermedia acuerdos legales</Alert.Title>
                            <Alert.Description fontSize="xs">
                              Cadencya no se responsabiliza del contenido ni de los acuerdos pactados entre las partes y no
                              intermedia ningún trato legal. Solo facilita la conexión entre entidad y banda.
                            </Alert.Description>
                          </Box>
                        </Alert.Root>

                        <Button
                          mt={4}
                          size="sm"
                          colorPalette="purple"
                          variant="outline"
                          onClick={() => setTabIndex(1)}><Icon as={FaPaperclip} />Ir a Documentos
                                              </Button>
                      </Box>

                      <Box bg={cardBg} borderWidth="1px" borderColor={surfaceBorder} rounded="2xl" p={5}>
                        <VStack align="stretch" gap={4}>
                          <Box>
                            <HStack gap={2} mb={1}>
                              <Icon as={FaFileAlt} color="purple.600" />
                              <Text fontWeight="800">Descripción</Text>
                            </HStack>
                            {acuerdo.descripcion ? (
                              <>
                                <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap" lineClamp={showFullDescripcion ? undefined : 6}>
                                  {acuerdo.descripcion}
                                </Text>
                                {String(acuerdo.descripcion).length > 420 && (
                                  <Button
                                    mt={2}
                                    size="sm"
                                    variant='plain'
                                    colorPalette="purple"
                                    onClick={() => setShowFullDescripcion((s) => !s)}
                                  >
                                    {showFullDescripcion ? 'Ver menos' : 'Ver más'}
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Text fontSize="sm" color={textColor}>—</Text>
                            )}
                          </Box>

                          <Separator />

                          <Box>
                            <HStack gap={2} mb={1}>
                              <Icon as={FaStickyNote} color="purple.600" />
                              <Text fontWeight="800">Notas</Text>
                            </HStack>
                            {acuerdo.notas ? (
                              <>
                                <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap" lineClamp={showFullNotas ? undefined : 4}>
                                  {acuerdo.notas}
                                </Text>
                                {String(acuerdo.notas).length > 240 && (
                                  <Button
                                    mt={2}
                                    size="sm"
                                    variant='plain'
                                    colorPalette="purple"
                                    onClick={() => setShowFullNotas((s) => !s)}
                                  >
                                    {showFullNotas ? 'Ver menos' : 'Ver más'}
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Text fontSize="sm" color={textColor}>—</Text>
                            )}
                          </Box>

                          {acuerdo.respuestaNotas && (
                            <>
                              <Separator />
                              <Box>
                                <HStack gap={2} mb={1}>
                                  <Icon as={FaStickyNote} color="blue.600" />
                                  <Text fontWeight="800">Notas de respuesta</Text>
                                </HStack>
                                <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap" lineClamp={4}>
                                  {acuerdo.respuestaNotas}
                                </Text>
                              </Box>
                            </>
                          )}
                        </VStack>
                      </Box>
                    </SimpleGrid>
                  </TabPanel>

                  <TabPanel value="1" p={0}>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                      <Box bg={cardBg} borderWidth="1px" borderColor={surfaceBorder} rounded="2xl" p={5}>
                        <HStack gap={2} mb={3}>
                          <Icon as={FaFileAlt} color="purple.600" />
                          <Text fontWeight="800">Archivos adjuntos</Text>
                          <Badge ml="auto" variant="subtle" colorPalette="purple" rounded="full">
                            {adjuntosList.length}
                          </Badge>
                        </HStack>

                        {adjuntosList.length > 0 ? (
                          <Wrap gap={3}>
                            {adjuntosList.map((item) => {
                              const href = resolveUploadsUrl(item.url);
                              const isImg =
                                (item.mime && String(item.mime).startsWith('image/')) ||
                                /\.(jpe?g|png|gif|webp)$/i.test(item.nombre || item.url);
                              return (
                                <Box
                                  key={item.url}
                                  borderWidth="1px"
                                  borderRadius="lg"
                                  p={2}
                                  bg={adjuntoPreviewCardBg}
                                  maxW="240px"
                                >
                                  {isImg ? (
                                    <Box as="a" href={href} target="_blank" rel="noopener noreferrer" display="block">
                                      <Image
                                        src={href}
                                        alt={item.nombre}
                                        maxH="120px"
                                        w="full"
                                        objectFit="cover"
                                        rounded="md"
                                      />
                                      <Text fontSize="xs" lineClamp={2} mt={1} color="gray.600">
                                        {item.nombre}
                                      </Text>
                                    </Box>
                                  ) : (
                                    <HStack as="a" href={href} target="_blank" rel="noopener noreferrer" align="start" gap={2}>
                                      <Icon as={FaFileAlt} mt={0.5} color="purple.500" />
                                      <Text fontSize="sm" color="blue.600" wordBreak="break-word">
                                        {item.nombre}
                                      </Text>
                                    </HStack>
                                  )}
                                </Box>
                              );
                            })}
                          </Wrap>
                        ) : (
                          <Text fontSize="sm" color={textColor}>Aún no hay documentos adjuntos.</Text>
                        )}
                      </Box>

                      <Box bg={cardBg} borderWidth="1px" borderColor={surfaceBorder} rounded="2xl" p={5}>
                        <HStack justify="space-between" mb={3}>
                          <HStack gap={2}>
                            <Icon as={FaPaperclip} color="purple.600" />
                            <Text fontWeight="800">Subir documentos</Text>
                          </HStack>
                          <Badge colorPalette={acuerdo.status === 'PENDING' ? 'orange' : 'gray'} rounded="full">
                            {getStatusText(acuerdo.status)}
                          </Badge>
                        </HStack>

                        {acuerdo.status !== 'PENDING' ? (
                          <Alert.Root
                            status="info"
                            variant='subtle'
                            borderRadius="lg"
                            borderStartWidth='3px'
                            borderStartColor='colorPalette.solid'>
                            <Alert.Indicator />
                            <Box>
                              <Alert.Title fontSize="sm">Adjuntos bloqueados</Alert.Title>
                              <Alert.Description fontSize="sm">
                                Una vez se acepta el acuerdo, ya no se pueden modificar archivos adjuntos. Solo es posible
                                adjuntar documentos mientras el acuerdo esté <strong>pendiente</strong>.
                              </Alert.Description>
                            </Box>
                          </Alert.Root>
                        ) : eventoYaEmpezo ? (
                          <Alert.Root
                            status="error"
                            variant='subtle'
                            borderRadius="lg"
                            borderStartWidth='3px'
                            borderStartColor='colorPalette.solid'>
                            <Alert.Indicator />
                            <Box>
                              <Alert.Title fontSize="sm">Evento ya iniciado</Alert.Title>
                              <Alert.Description fontSize="sm">
                                El evento ya ha pasado, por lo que no se pueden añadir ni eliminar archivos adjuntos.
                              </Alert.Description>
                            </Box>
                          </Alert.Root>
                        ) : (
                          <VStack align="stretch" gap={3}>
                            <Alert.Root
                              status="info"
                              variant='subtle'
                              borderRadius="lg"
                              borderStartWidth='3px'
                              borderStartColor='colorPalette.solid'>
                              <Alert.Indicator />
                              <Box>
                                <Alert.Title fontSize="sm">Importante</Alert.Title>
                                <Alert.Description fontSize="sm">
                                  Cuando el acuerdo se acepte, <strong>se bloquearán</strong> los cambios en los adjuntos.
                                </Alert.Description>
                              </Box>
                            </Alert.Root>
                            <input
                              ref={inputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              accept=".pdf,.txt,.md,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,image/*"
                              onChange={handlePickAdjuntos}
                            />

                            <HStack>
                              <Button
                                size="sm"
                                colorPalette="purple"
                                onClick={() => inputRef.current?.click()}
                                disabled={newAdjuntosFiles.length >= MAX_ACUERDO_ADJUNTOS_UI || subiendoAdjuntos}><Icon as={FaPaperclip} />Seleccionar archivos
                                                          </Button>
                              <Text fontSize="xs" color="gray.500">
                                Máximo {MAX_ACUERDO_ADJUNTOS_UI} archivos.
                              </Text>
                            </HStack>

                            {newAdjuntosFiles.length > 0 && (
                              <VStack align="stretch" gap={2}>
                                {newAdjuntosFiles.slice(0, 4).map((f, idx) => (
                                  <HStack
                                    key={`${f.name}-${f.size}-${f.lastModified}`}
                                    justify="space-between"
                                    py={2}
                                    px={3}
                                    rounded="lg"
                                    bg={fileRowBg}
                                    borderWidth="1px"
                                    borderColor={surfaceBorder}
                                  >
                                    <HStack gap={2} minW={0}>
                                      <Icon as={FaFileAlt} color="purple.500" />
                                      <Text fontSize="sm" lineClamp={1}>{f.name}</Text>
                                    </HStack>
                                    <IconButton
                                      aria-label="Quitar archivo"
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => setNewAdjuntosFiles((p) => p.filter((_, i) => i !== idx))}><Icon as={FaTimes} /></IconButton>
                                  </HStack>
                                ))}
                                {newAdjuntosFiles.length > 4 && (
                                  <Text fontSize="xs" color="gray.500">
                                    +{newAdjuntosFiles.length - 4} archivo(s) más…
                                  </Text>
                                )}
                              </VStack>
                            )}

                            <Alert.Root
                              status="warning"
                              variant='subtle'
                              borderRadius="lg"
                              borderStartWidth='3px'
                              borderStartColor='colorPalette.solid'>
                              <Alert.Indicator />
                              <Box>
                                <Alert.Title fontSize="sm">Aviso legal</Alert.Title>
                                <Alert.Description fontSize="xs">
                                  Cadencya no se responsabiliza del contenido ni intermedia ningún trato legal. Solo facilita la
                                  conexión entre ambas partes.
                                </Alert.Description>
                              </Box>
                            </Alert.Root>

                            <Field.Root required>
                              <Checkbox.Root
                                onCheckedChange={(details) => setAceptaDisclaimerAdjuntos(details.checked === true)}
                                checked={aceptaDisclaimerAdjuntos}
                              ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>He leído y acepto el aviso legal antes de adjuntar documentos
                                                            </Checkbox.Label></Checkbox.Root>
                            </Field.Root>

                            <Button
                              colorPalette="green"
                              onClick={handleUploadAdjuntos}
                              loading={subiendoAdjuntos}
                              disabled={subiendoAdjuntos || newAdjuntosFiles.length === 0 || !aceptaDisclaimerAdjuntos}
                            >
                              Subir documentos
                            </Button>
                          </VStack>
                        )}
                      </Box>
                    </SimpleGrid>
                  </TabPanel>

                  <TabPanel value="2" p={0}>
                    <Box bg={cardBg} borderWidth="1px" borderColor={surfaceBorder} rounded="2xl" p={5}>
                      <HStack gap={2} mb={3}>
                        <Icon as={FaCalendarCheck} color="purple.600" />
                        <Text fontWeight="800">Evento</Text>
                      </HStack>

                      {evento ? (
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                          <Box p={4} rounded="xl" bg={eventoCardBg} borderWidth="1px" borderColor={eventoCardBorder}>
                            <Text fontSize="xs" fontWeight="700" color={eventoLabelColor} textTransform="uppercase">
                              Evento
                            </Text>
                            <Text fontWeight="800" color={eventoTitleColor}>
                              {evento.eventName}
                            </Text>
                          </Box>
                          <Box p={4} rounded="xl" bg={eventoCardBg} borderWidth="1px" borderColor={eventoCardBorder}>
                            <HStack gap={2} mb={1}>
                              <Icon as={FaClock} color={eventoIconColor} />
                              <Text fontSize="xs" fontWeight="700" color={eventoLabelColor} textTransform="uppercase">
                                Fecha
                              </Text>
                            </HStack>
                            <Text fontWeight="700" color={eventoTitleColor}>
                              {new Date(evento.date).toLocaleDateString('es-ES', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </Text>
                          </Box>
                          <Box p={4} rounded="xl" bg={eventoCardBg} borderWidth="1px" borderColor={eventoCardBorder} gridColumn={{ base: 'auto', md: '1 / -1' }}>
                            <HStack gap={2} mb={1}>
                              <Icon as={FaMapMarkerAlt} color={eventoIconColor} />
                              <Text fontSize="xs" fontWeight="700" color={eventoLabelColor} textTransform="uppercase">
                                Ubicación
                              </Text>
                            </HStack>
                            <Text fontWeight="700" color={eventoTitleColor}>
                              {evento.location || '—'}
                            </Text>
                          </Box>
                        </SimpleGrid>
                      ) : (
                        <Text fontSize="sm" color={textColor}>Este acuerdo no tiene evento asociado.</Text>
                      )}
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs.Root>
            </Dialog.Body>

            <Dialog.Footer bg={footerBg} roundedBottom="md">
              <Button onClick={onClose} colorPalette="purple" variant="outline">
                Cerrar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
};

export default AcuerdoDetailsModal;
